import { UI } from "components"
import useChatwoot, { resetChatwootWidget, CHATWOOT_BASE_URL } from "hooks/useChatwoot"
import useUser from "hooks/useUser"
import AtualizarDados from "pages/atualizar-dados"
import Adhesion from "pages/adhesion"
import Adjusts from "pages/adjusts"
import Auth from "pages/auth"
import Bonus from "pages/bonus"
import Distributors from "pages/distributors"
import Financial from "pages/financial"
import Home from "pages/home"
import Identity from "pages/identity"
import Logout from "pages/logout"
import Network from "pages/network"
import Onboarding from "pages/onboarding"
import Orders from "pages/orders"
import Points from "pages/points"
import Profile from "pages/profile"
import Recovery from "pages/recovery"
import Remote from "pages/remote"
import Store from "pages/store"
import Terms from "pages/terms"
import Vouchers from "pages/vouchers"
import { useCallback, useEffect, useRef, useState } from "react"
import { Route, Routes as BrowserRoutes } from "react-router-dom"

function ChatwootInit() {
  useChatwoot()
  const { data: user } = useUser()
  const pendingReset = useRef(false)
  const resetTimer = useRef(null)
  // Controla se setUser já foi chamado para evitar que o hide sobrescreva o show
  const identified = useRef(false)

  // Extraído como callback para poder ser chamado novamente após reset()
  const identifyUser = useCallback(() => {
    if (!user || !window.$chatwoot) return
    window.$chatwoot.setUser(String(user.id), {
      name: user.name,
      email: user.email,
      phone_number: user.mobile
        ? `+${user.mobile_country_code ?? "55"}${user.mobile}`
        : undefined,
    })
    identified.current = true
  }, [user])

  // Esconde o widget imediatamente ao inicializar para impedir conversas com
  // contatos anônimos (apelidos gerados pelo Chatwoot)
  useEffect(() => {
    const hide = () => {
      if (!identified.current) try { window.$chatwoot?.toggleBubbleVisibility('hide') } catch {}
    }
    if (window.$chatwoot) {
      hide()
    } else {
      window.addEventListener("chatwoot:ready", hide, { once: true })
      return () => window.removeEventListener("chatwoot:ready", hide)
    }
  }, [])

  // Identifica o usuário assim que o widget estiver pronto ou o user carregar
  useEffect(() => {
    if (!user) return
    if (window.$chatwoot) {
      identifyUser()
    } else {
      window.addEventListener("chatwoot:ready", identifyUser, { once: true })
      return () => window.removeEventListener("chatwoot:ready", identifyUser)
    }
  }, [user, identifyUser])

  // Reset do widget após o usuário submeter o CSAT de uma conversa resolvida
  useEffect(() => {
    const scheduleReset = () => {
      pendingReset.current = false
      clearTimeout(resetTimer.current)
      // Aguarda 1.5s para o widget renderizar a tela de "obrigado" antes de resetar
      resetTimer.current = setTimeout(() => {
        resetChatwootWidget()
        // Re-identifica o usuário para que a próxima conversa já apareça vinculada à conta
        setTimeout(identifyUser, 300)
      }, 1500)
    }

    const handleConversationResolved = () => {
      // Conversa resolvida: o widget vai exibir o formulário de CSAT a seguir
      pendingReset.current = true
    }

    const handlePostMessage = (event) => {
      // Só processa mensagens vindas do iframe do Chatwoot
      if (event.origin !== CHATWOOT_BASE_URL) return
      if (!pendingReset.current) return

      let data
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data
      } catch {
        return
      }

      // O Chatwoot emite um postMessage com event contendo "csat" ao submeter o formulário.
      // Nomes observados: "csat-submitted", "csat_submitted", "conversation:csat-submitted".
      // Se o nome mudar em versões futuras, inspecione window.addEventListener("message", ...)
      // no DevTools com pendingReset = true para capturar o evento correto.
      const eventName = String(data?.event ?? data?.eventName ?? data?.type ?? "")
      if (eventName.toLowerCase().includes("csat")) {
        scheduleReset()
      }
    }

    window.addEventListener("chatwoot:on-conversation-resolved", handleConversationResolved)
    window.addEventListener("message", handlePostMessage)

    return () => {
      window.removeEventListener("chatwoot:on-conversation-resolved", handleConversationResolved)
      window.removeEventListener("message", handlePostMessage)
      clearTimeout(resetTimer.current)
    }
  }, [identifyUser])

  return null
}

export default function Routes(){

    const token = localStorage.getItem('@token')

    if(!!token) return <PrivateRoutes />
    
    return <PublicRoutes />

}

const PrivateRoutes = () => {
    const { data: user, isLoading } = useUser()
    const [updateSkipped, setUpdateSkipped] = useState(false)

    if (isLoading) return null

    const needsUpdate = user?.update_data_at === null
    const overdue = user?.mandatory_update_overdue === true

    if (needsUpdate && (!updateSkipped || overdue)) {
        return (
            <AtualizarDados
                daysRemaining={user?.mandatory_update_days_remaining}
                overdue={overdue}
                onSkip={!overdue ? () => setUpdateSkipped(true) : undefined}
            />
        )
    }

    return(
    <UI.Layout>
        <ChatwootInit />
        <BrowserRoutes>

            {/* Profile */}
            <Route path="/perfil" element={<Profile />} />

            {/* Financial */}
            <Route path="/financeiro" element={<Financial />} />

            {/* Bonificações */}
            <Route path="/bonus" element={<Bonus />} />
        
            {/* Rede */}
            <Route path="/rede" element={<Network />} />

            {/* Pontuação */}
            <Route path="/pontos" element={<Points />} />

            {/* Pedidos */}
            <Route path="/pedidos/*" element={<Orders />} />

            {/* Vouchers */}
            <Route path="/vouchers" element={<Vouchers />} />
            
            {/* Distributors */}
            <Route path="/loja/nucleos/*" element={<Distributors /> } />

            {/* Store/ */}
            <Route path="/loja/*" element={<Store /> } />

            {/* Adesão */}
            <Route path="/adesao/*" element={<Adhesion />} />

            {/* Adesão */}
            <Route path="/identidade/*" element={<Identity />} />

            {/* Ajustes */}
            <Route path="/ajustes" element={<Adjusts />} />

            {/* Logout */}
            <Route path="/logout" element={<Logout />} />

            {/* Onboarding */}
            <Route path="/onboarding/:id" element={<Onboarding />} />
            <Route path="/onboarding/*" element={<Onboarding />} />
            <Route path="/rede/:id" element={<Onboarding />} />

            {/* Termos */}
            <Route path="/termos" element={<Terms />} />

            <Route index element={<Home />} />

        </BrowserRoutes>
    </UI.Layout>
    )
}

const PublicRoutes = () => {
    return(
    <BrowserRoutes>

        {/* Authenticate */}
        <Route index element={<Auth />} />

        {/* Recuperação de senha */}
        <Route path="/recuperacao" element={<Recovery />} />

        {/* Onboarding */}
        <Route path="/onboarding/:id" element={<Onboarding />} />
        <Route path="/onboarding/*" element={<Onboarding />} />
        <Route path="/rede/:id" element={<Onboarding />} />

        {/* Adesão */}
        <Route path="/adesao/*" element={<Adhesion />} />

        {/* Acesso remoto */}
        <Route path="support/remote" element={<Remote />} />

        <Route index path="*" element={<Auth />} />
    </BrowserRoutes>
    )
}